import { useCallback, useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

/** How close to an edge (px) a scroll has to come before the next page loads. */
const EDGE_PX = 120;
/** A touch drag this long (px) toward an edge counts as a pull for more. */
const TOUCH_PULL_PX = 30;

/** An item's offset from the top of the scroll container's visible area. */
function itemTop(root: HTMLElement, id: string): number | null {
  const item = root.querySelector<HTMLElement>(`[data-msg-id="${CSS.escape(id)}"]`);
  return item ? item.getBoundingClientRect().top - root.getBoundingClientRect().top : null;
}

interface Options {
  /** The thread's own scroll container; each item in it carries `data-msg-id`. */
  scrollRef: RefObject<HTMLElement>;
  /** False until the container is rendered, so listeners attach once it exists. */
  active: boolean;
  /** Id of the thread's first item; it changes when an older page is prepended. */
  firstItemId: string | null;
  hasOlder: boolean;
  isLoadingOlder: boolean;
  onLoadOlder?: () => void;
  hasNewer?: boolean;
  isLoadingNewer?: boolean;
  onLoadNewer?: () => void;
}

/**
 * Loads a thread's older and newer pages when the reader reaches an edge, as
 * SMS and email threads do. Scrolling near the top loads older, near the bottom
 * newer. The wheel and touch drags at an edge load too, so a thread that fits
 * without scrolling still pages. After an older page lands, the view stays on
 * the message the reader was looking at. Returns loaders for the pager pills.
 */
export function useThreadEdgePaging({
  scrollRef,
  active,
  firstItemId,
  hasOlder,
  isLoadingOlder,
  onLoadOlder,
  hasNewer = false,
  isLoadingNewer = false,
  onLoadNewer,
}: Options) {
  // Latest values for the listeners, which attach once per container.
  const loadOlderRef = useRef(onLoadOlder);
  const loadNewerRef = useRef(onLoadNewer);
  loadOlderRef.current = onLoadOlder;
  loadNewerRef.current = onLoadNewer;
  const isLoadingOlderRef = useRef(isLoadingOlder);
  isLoadingOlderRef.current = isLoadingOlder;
  const isLoadingNewerRef = useRef(isLoadingNewer);
  isLoadingNewerRef.current = isLoadingNewer;
  const hasOlderRef = useRef(hasOlder);
  hasOlderRef.current = hasOlder;
  const hasNewerRef = useRef(hasNewer);
  hasNewerRef.current = hasNewer;

  // Held while a page is in flight, so a burst of scroll events loads it once.
  const firingOlderRef = useRef(false);
  const firingNewerRef = useRef(false);
  useEffect(() => {
    if (!isLoadingOlder) firingOlderRef.current = false;
  }, [isLoadingOlder]);
  useEffect(() => {
    if (!isLoadingNewer) firingNewerRef.current = false;
  }, [isLoadingNewer]);
  // The scroll that keeps the view in place after a page lands is not the reader's.
  const suppressNextScrollRef = useRef(false);

  // Keep the reader's place when an older page is added above them: scroll by
  // exactly how far the page pushed the old first item down the list. The
  // item's offset within the list only moves for a prepend, so scrolling while
  // the page loads, or a newer page landing below meanwhile, is left as is.
  const firstItemIdRef = useRef(firstItemId);
  firstItemIdRef.current = firstItemId;
  const anchorRef = useRef<{ id: string; offset: number } | null>(null);
  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const el = scrollRef.current;
    if (!anchor || !el || firstItemId === anchor.id) return;
    anchorRef.current = null;
    const top = itemTop(el, anchor.id);
    if (top != null) el.scrollTop += top + el.scrollTop - anchor.offset;
  }, [firstItemId, scrollRef]);

  const loadOlder = useCallback(() => {
    const el = scrollRef.current;
    if (!el || isLoadingOlderRef.current || firingOlderRef.current) return;
    if (!hasOlderRef.current || !loadOlderRef.current) return;
    firingOlderRef.current = true;
    const id = firstItemIdRef.current;
    const top = id ? itemTop(el, id) : null;
    anchorRef.current = id && top != null ? { id, offset: top + el.scrollTop } : null;
    suppressNextScrollRef.current = true;
    loadOlderRef.current();
  }, [scrollRef]);

  const loadNewer = useCallback(() => {
    if (isLoadingNewerRef.current || firingNewerRef.current) return;
    if (!hasNewerRef.current || !loadNewerRef.current) return;
    firingNewerRef.current = true;
    suppressNextScrollRef.current = true;
    loadNewerRef.current();
  }, []);

  useEffect(() => {
    const root = scrollRef.current;
    if (!active || !root) return;
    let prevScrollTop = root.scrollTop;
    // A short grace after opening, so the initial jump to the latest or the
    // opened message doesn't count as the reader reaching an edge.
    let ready = false;
    const readyTimer = setTimeout(() => {
      ready = true;
      prevScrollTop = root.scrollTop;
    }, 250);

    const atTop = () => root.scrollTop <= EDGE_PX;
    const atBottom = () => root.scrollHeight - root.scrollTop - root.clientHeight <= EDGE_PX;

    const onScroll = () => {
      const cur = root.scrollTop;
      const delta = cur - prevScrollTop;
      prevScrollTop = cur;
      if (suppressNextScrollRef.current) {
        suppressNextScrollRef.current = false;
        return;
      }
      if (!ready || delta === 0) return;
      if (delta < 0 && atTop()) loadOlder();
      else if (delta > 0 && atBottom()) loadNewer();
    };

    // Wheel and touch reach an edge even when the thread fits and cannot scroll.
    const onWheel = (e: WheelEvent) => {
      if (!ready) return;
      if (e.deltaY < 0 && atTop()) loadOlder();
      else if (e.deltaY > 0 && atBottom()) loadNewer();
    };
    let touchStartY: number | null = null;
    const onTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0]?.clientY ?? null;
    };
    const onTouchMove = (e: TouchEvent) => {
      const y = e.touches[0]?.clientY;
      if (!ready || touchStartY == null || y == null) return;
      // A finger moving down pulls the content down, toward older messages.
      if (y - touchStartY > TOUCH_PULL_PX && atTop()) loadOlder();
      else if (touchStartY - y > TOUCH_PULL_PX && atBottom()) loadNewer();
    };

    root.addEventListener('scroll', onScroll, { passive: true });
    root.addEventListener('wheel', onWheel, { passive: true });
    root.addEventListener('touchstart', onTouchStart, { passive: true });
    root.addEventListener('touchmove', onTouchMove, { passive: true });
    return () => {
      clearTimeout(readyTimer);
      root.removeEventListener('scroll', onScroll);
      root.removeEventListener('wheel', onWheel);
      root.removeEventListener('touchstart', onTouchStart);
      root.removeEventListener('touchmove', onTouchMove);
    };
  }, [active, scrollRef, loadOlder, loadNewer]);

  return { loadOlder, loadNewer };
}
